// @flow
import React, { Component, cloneElement, createRef, type Node } from 'react';
import { get, inRange, isEqual, noop } from 'lodash';

import { ARROW_LEFT, ARROW_RIGHT } from 'Utils/keyCodes';
import { getIndexInRange } from './SliderHandler.helper';

import { SliderHandlerItems } from './SliderHandlerItems.jsx';

type Props = {
  children: (props: *) => Node,
  className?: string,
  items: Node,
  hasAutoplay?: boolean,
  hasInfiniteScroll?: boolean,
  hasKeyboardSupport?: boolean,
  hasLazyload?: boolean,
  hasSwipeSupport?: boolean,
  initialIndex: number,
  nbItemsToScroll?: number,
  nbItemsToShow: number,
  onNext?: (e?: Event) => void,
  onPrevious?: (e?: Event) => void,
};

type State = {
  index: number,
  isSwiping: boolean,
  isRTL: boolean,
  itemsCount: number,
  itemsToRender: Array<boolean>,
  lastIndex: number,
  maxIndex: number,
  slideWidth: number,
  swipeStart: number,
  swipeStartTime: Date,
  swipeX: number,
};

export class SliderHandler extends Component<Props, State> {
  static defaultProps = {
    initialIndex: 0,
    nbItemsToScroll: 1,
    nbItemsToShow: 1,
  };

  sliderRef: { current: ?HTMLDivElement };
  intervalId: IntervalID;

  constructor(props: Props) {
    super(props);

    this.sliderRef = createRef();

    this.state = this.initState(props);
  }

  componentDidMount() {
    const { hasAutoplay, hasKeyboardSupport } = this.props;

    if (hasKeyboardSupport && this.sliderRef.current) {
      this.sliderRef.current.addEventListener('keyup', this.onKeyUp, false);
    }

    if (hasAutoplay) {
      this.intervalId = setInterval(() => this.onNext(), 3000);
    }

    if (get(document, 'documentElement.dir', 'ltr') === 'rtl') {
      this.setState({
        isRTL: true,
      });
    }
  }

  componentWillUnmount() {
    const { hasKeyboardSupport } = this.props;

    if (hasKeyboardSupport && this.sliderRef.current) {
      this.sliderRef.current.removeEventListener('keyup', this.onKeyUp, false);
    }

    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  initState(props: Props) {
    const { hasLazyload, initialIndex, items, nbItemsToShow } = props;

    const itemsCount = items.length;
    const maxIndex = itemsCount < nbItemsToShow ? 0 : itemsCount - nbItemsToShow;
    const index = getIndexInRange(initialIndex, maxIndex);
    const initialItemsToRender = Array(itemsCount).fill(!hasLazyload, 0, itemsCount);
    const itemsToRender = this.getItemsToRender(initialItemsToRender, index, nbItemsToShow);

    return {
      index,
      isSwiping: false,
      isRTL: false,
      itemsCount,
      itemsToRender,
      lastIndex: index,
      maxIndex,
      slideWidth: 0,
      swipeStart: 0,
      swipeStartTime: new Date(),
      swipeX: 0,
    };
  }

  getItemsToRender(
    itemsToRender: Array<boolean>,
    index: number,
    nbItemsToShow: number,
  ): Array<boolean> {
    const { hasLazyload } = this.props;
    const step = this.getStep();
    const minRange = index - step;
    const maxRange = index + nbItemsToShow + step;
    const newItemToRender = itemsToRender.map(
      (isRendered, itemIndex) => isRendered || inRange(itemIndex, minRange, maxRange),
    );

    if (
      !hasLazyload ||
      itemsToRender.every(isRendered => isRendered) ||
      isEqual(itemsToRender, newItemToRender)
    ) {
      return itemsToRender;
    }

    return newItemToRender;
  }

  getStep(): number {
    const { nbItemsToScroll = 1 } = this.props;
    return nbItemsToScroll > 0 ? nbItemsToScroll : 1;
  }

  // Slide
  getItemPosition(): number {
    const { index, swipeX, isSwiping, isRTL } = this.state;
    const x = isSwiping ? swipeX : index;
    const posX = (x * 100) / this.props.nbItemsToShow;

    return isRTL ? posX : -posX;
  }

  goToIndex(index: number, callback: (e?: Event) => void = noop) {
    this.setState(prevState => {
      const itemsToRender = this.getItemsToRender(
        prevState.itemsToRender,
        index,
        this.props.nbItemsToShow,
      );

      return {
        index,
        itemsToRender,
        lastIndex: index,
      };
    }, callback);
  }

  // Events handlers
  onNext = (event?: SyntheticEvent<> | KeyboardEvent) => {
    if (event) {
      event.preventDefault();
    }
    const { maxIndex, index } = this.state;
    let i = index + this.getStep();

    if (i > maxIndex) {
      i = this.props.hasInfiniteScroll ? 0 : maxIndex;
    }
    this.goToIndex(i, this.props.onNext);
  };

  onPrevious = (event?: SyntheticEvent<> | KeyboardEvent) => {
    if (event) {
      event.preventDefault();
    }
    const { maxIndex, index } = this.state;
    const min = 0;
    let i = index - this.getStep();

    if (i < min) {
      i = this.props.hasInfiniteScroll ? maxIndex + i + 1 : min;
    }
    this.goToIndex(i, this.props.onPrevious);
  };

  onKeyUp = (event: KeyboardEvent) => {
    const { isRTL } = this.state;
    if (this.props.hasKeyboardSupport) {
      switch (event.which) {
        case ARROW_RIGHT:
          event.stopPropagation();
          isRTL ? this.onPrevious(event) : this.onNext(event);
          break;
        case ARROW_LEFT:
          event.stopPropagation();
          isRTL ? this.onNext(event) : this.onPrevious(event);
          break;
        default:
      }
    }
  };

  // Swipe
  getSwipeX(event: TouchEvent) {
    return event.touches[0].pageX;
  }

  onSwipeStart = (event: TouchEvent) => {
    if (this.props.hasSwipeSupport) {
      event.stopPropagation();

      const swipeStart = this.getSwipeX(event);
      const slideWidth = this.sliderRef.current ? this.sliderRef.current.offsetWidth : 0;

      this.setState(({ lastIndex }) => {
        return {
          slideWidth,
          isSwiping: true,
          swipeStart,
          swipeX: lastIndex,
          swipeStartTime: new Date(),
        };
      });
    }
  };

  onSwipeMove = (event: TouchEvent) => {
    if (this.props.hasSwipeSupport) {
      event.stopPropagation();

      const x = this.getSwipeX(event);

      this.setState(prevState => {
        const {
          itemsToRender: prevItemsToRender,
          lastIndex,
          swipeStart,
          slideWidth,
          maxIndex,
          isRTL,
        } = prevState;

        const offset = swipeStart - x;
        const percentageOffset = offset / slideWidth;

        const swipeX = isRTL ? lastIndex - percentageOffset : lastIndex + percentageOffset;
        const index = getIndexInRange(Math.round(swipeX), maxIndex);
        const itemsToRender = this.getItemsToRender(
          prevItemsToRender,
          index,
          this.props.nbItemsToShow,
        );

        return {
          swipeX: getIndexInRange(swipeX, maxIndex),
          ...(itemsToRender && { itemsToRender }),
        };
      });
    }
  };

  onSwipeEnd = (event: TouchEvent) => {
    if (this.props.hasSwipeSupport) {
      event.stopPropagation();

      this.setState(
        prevState => {
          const {
            itemsToRender: prevItemsToRender,
            lastIndex,
            swipeStartTime,
            swipeX,
            maxIndex,
          } = prevState;

          const step = this.getStep();
          const timeElapsed = new Date().getTime() - swipeStartTime.getTime();
          const offset = lastIndex - swipeX;
          const velocity = Math.round((offset / timeElapsed) * 10000);

          let index = Math.round(swipeX);

          if (Math.abs(velocity) > 5) {
            index = velocity < 0 ? lastIndex + step : lastIndex - step;
          }

          const itemsToRender = this.getItemsToRender(
            prevItemsToRender,
            index,
            this.props.nbItemsToShow,
          );

          return {
            index: getIndexInRange(index, maxIndex),
            isSwiping: false,
            swipeStart: 0,
            swipeX: 0,
            ...(itemsToRender && { itemsToRender }),
          };
        },
        () => {
          const { index, lastIndex } = this.state;
          const { onPrevious, onNext } = this.props;

          if (index < lastIndex && onPrevious) {
            onPrevious();
          } else if (index > lastIndex && onNext) {
            onNext();
          }

          this.setState(() => ({ lastIndex: index }));
        },
      );
    }
  };

  // Visibility
  isItemVisible = (itemIndex: number): boolean => {
    const { index } = this.state;
    return itemIndex >= index && itemIndex < index + this.props.nbItemsToShow;
  };

  isFirstItemVisible(): boolean {
    return this.isItemVisible(0);
  }

  isLastItemVisible(): boolean {
    const { itemsCount } = this.state;
    return this.isItemVisible(itemsCount - 1);
  }

  hasControls(): boolean {
    return this.state.itemsCount > this.props.nbItemsToShow;
  }

  shouldRenderItem = (itemIndex: number): boolean => {
    return !this.props.hasLazyload || this.state.itemsToRender[itemIndex];
  };

  // render
  render() {
    const { index, isSwiping, itemsCount } = this.state;

    if (!itemsCount) return null;

    const { children, className, hasInfiniteScroll, items, nbItemsToShow } = this.props;

    const hasControls = this.hasControls();

    const itemsNode = (
      <SliderHandlerItems
        className={className}
        hasControls={hasControls}
        isSwiping={isSwiping}
        itemPercentSize={100 / nbItemsToShow}
        onSwipeEnd={this.onSwipeEnd}
        onSwipeMove={this.onSwipeMove}
        onSwipeStart={this.onSwipeStart}
        position={this.getItemPosition()}
        ref={this.sliderRef}
      >
        {items
          .map(
            (item, idx) =>
              this.shouldRenderItem(idx)
                ? cloneElement(
                    item,
                    this.isItemVisible(idx) ? {} : { 'aria-hidden': true, tabIndex: -1 },
                  )
                : null,
          )
          .filter(Boolean)}
      </SliderHandlerItems>
    );

    const childrenProps = {
      hasControls,
      index,
      isNextControlDisabled: !hasInfiniteScroll && this.isLastItemVisible(),
      isPreviousControlDisabled: !hasInfiniteScroll && this.isFirstItemVisible(),
      itemsNode,
      onNext: this.onNext,
      onPrevious: this.onPrevious,
    };

    return children(childrenProps);
  }
}
